import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../../components/Modal';
import {
  deleteCustomerUser,
  listCustomerUsers,
  updateCustomerUser,
  type CustomerUserRecord,
} from '../../services/users';

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
};

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
};

const EMPTY_EMAIL_CHANGE_STATE = {
  userId: '',
  currentEmail: '',
  pendingEmail: '',
  sentAt: null as string | null,
};

export default function UserManager() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [emailChangeState, setEmailChangeState] = useState(EMPTY_EMAIL_CHANGE_STATE);

  const {
    data,
    error,
    isError,
    isLoading,
    refetch,
  } = useQuery<CustomerUserRecord[]>({
    queryKey: ['people', 'customers', search],
    queryFn: () => listCustomerUsers(search),
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: async (user: CustomerUserRecord) => deleteCustomerUser(user.id),
    onSuccess: async (_data, user) => {
      await queryClient.invalidateQueries({ queryKey: ['people', 'customers'] });
      if (selectedUserId === user.id) {
        setSelectedUserId(null);
        setModalOpen(false);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: {
      userId: string;
      fullName: string;
      email: string;
      phone: string;
    }) => updateCustomerUser(input),
    onSuccess: async (updatedUser) => {
      await queryClient.invalidateQueries({ queryKey: ['people', 'customers'] });
      setSelectedUserId(updatedUser.id);
      setEditForm({
        fullName: updatedUser.fullName ?? '',
        email: updatedUser.email,
        phone: updatedUser.phone ?? '',
      });
      if (updatedUser.pendingEmail) {
        setEmailChangeState({
          userId: updatedUser.id,
          currentEmail: updatedUser.email,
          pendingEmail: updatedUser.pendingEmail,
          sentAt: updatedUser.emailChangeSentAt ?? null,
        });
      } else {
        setEmailChangeState(EMPTY_EMAIL_CHANGE_STATE);
      }
    },
  });

  const users = useMemo(() => data ?? [], [data]);
  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId) ?? null,
    [selectedUserId, users],
  );

  useEffect(() => {
    if (!selectedUser) {
      setEditForm(EMPTY_FORM);
      return;
    }

    setEditForm({
      fullName: selectedUser.fullName ?? '',
      email: selectedUser.email,
      phone: selectedUser.phone ?? '',
    });
  }, [selectedUser]);

  const openManage = (userId: string) => {
    setSelectedUserId(userId);
    setModalOpen(true);
  };

  const closeManage = () => {
    setModalOpen(false);
    setSelectedUserId(null);
    setEditForm(EMPTY_FORM);
    setEmailChangeState(EMPTY_EMAIL_CHANGE_STATE);
  };

  const activePendingEmail =
    emailChangeState.userId === selectedUserId && emailChangeState.pendingEmail
      ? emailChangeState
      : null;

  const hasUnsavedChanges = useMemo(() => {
    if (!selectedUser) return false;

    return (
      editForm.fullName.trim() !== (selectedUser.fullName ?? '') ||
      editForm.email.trim().toLowerCase() !== selectedUser.email.toLowerCase() ||
      editForm.phone.trim() !== (selectedUser.phone ?? '')
    );
  }, [editForm, selectedUser]);

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Müşteri kullanıcıları</p>
            <p className="card-subtitle">
              {isLoading ? 'Kullanıcılar yükleniyor...' : `${users.length} müşteri hesabı`}
            </p>
          </div>
          <button type="button" className="button ghost" onClick={() => void refetch()}>
            Yenile
          </button>
        </div>

        <div className="form-row center">
          <label className="form-field">
            <span>Ara</span>
            <input
              type="search"
              placeholder="Ad, e-posta veya telefon"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        {isError ? (
          <div className="empty-state">
            <strong>Kullanıcı listesi alınamadı</strong>
            <p>{error instanceof Error ? error.message : 'Beklenmeyen bir hata oluştu.'}</p>
          </div>
        ) : isLoading ? (
          <div className="empty-state">
            <strong>Liste hazırlanıyor</strong>
            <p>Supabase kullanıcı kayıtları okunuyor.</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <strong>Kullanıcı bulunamadı</strong>
            <p>
              {search.trim().length >= 2
                ? 'Arama kriterine uyan müşteri hesabı yok.'
                : 'Henüz listelenecek müşteri hesabı görünmüyor.'}
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>E-posta</th>
                  <th>Telefon</th>
                  <th>Oluşturulma</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const deleting =
                    deleteMutation.isPending && deleteMutation.variables?.id === user.id;

                  return (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.fullName ?? 'İsimsiz kullanıcı'}</strong>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phone ?? '—'}</td>
                      <td>{formatDateTime(user.createdAt)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="button secondary"
                            onClick={() => openManage(user.id)}
                          >
                            Yönet
                          </button>
                          <button
                            type="button"
                            className="button danger"
                            disabled={deleting || updateMutation.isPending}
                            onClick={() => {
                              const confirmed = window.confirm(
                                `${user.email} hesabı kalıcı olarak silinsin mi?\n\nBu işlemden sonra kullanıcı giriş yapamaz. Geçmiş siparişler ve muhasebe verileri korunur.`
                              );
                              if (!confirmed) return;
                              deleteMutation.mutate(user);
                            }}
                          >
                            {deleting ? 'Siliniyor…' : 'Hesabı sil'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="info-banner">
          Bu ekran yalnızca müşteri hesaplarını listeler.
          Personel, sürücü ve admin hesapları ayrı olarak Personel/Kuryeler ekranlarından yönetilir.
        </div>
      </div>

      <Modal
        open={modalOpen && selectedUser !== null}
        onClose={closeManage}
        title={selectedUser ? `${selectedUser.fullName ?? selectedUser.email} — Müşteri profili` : 'Müşteri profili'}
        wide
      >
        {selectedUser ? (
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              if (!selectedUser) return;

              updateMutation.mutate({
                userId: selectedUser.id,
                fullName: editForm.fullName.trim(),
                email: editForm.email.trim(),
                phone: editForm.phone.trim(),
              });
            }}
          >
            {updateMutation.isError ? (
              <p className="error-text">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'Kullanıcı bilgileri güncellenemedi.'}
              </p>
            ) : null}

            {deleteMutation.isError ? (
              <p className="error-text">
                {deleteMutation.error instanceof Error
                  ? deleteMutation.error.message
                  : 'Kullanıcı silinemedi.'}
              </p>
            ) : null}

            <div className="form-row">
              <label className="form-field">
                <span>Ad soyad</span>
                <input
                  type="text"
                  value={editForm.fullName}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, fullName: event.target.value }))
                  }
                  disabled={updateMutation.isPending}
                />
              </label>
              <label className="form-field">
                <span>Telefon</span>
                <input
                  type="tel"
                  placeholder="+90 5xx xxx xx xx"
                  value={editForm.phone}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, phone: event.target.value }))
                  }
                  disabled={updateMutation.isPending}
                />
              </label>
            </div>

            <label className="form-field">
              <span>E-posta</span>
              <input
                type="email"
                value={editForm.email}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, email: event.target.value }))
                }
                disabled={updateMutation.isPending}
              />
            </label>

            <div className="profile-grid">
              <div className="profile-row">
                <strong>Kayıt tarihi</strong>
                <span>{formatDateTime(selectedUser.createdAt)}</span>
              </div>
              <div className="profile-row">
                <strong>Silme etkisi</strong>
                <span>Giriş kapanır, sipariş geçmişi korunur</span>
              </div>
            </div>

            <div className="info-banner">
              E-posta güncellemesi artık doğrulama gerektirir. Telefon güncellemesi ise OTP akışı tamamlanana kadar
              admin tarafından doğrudan uygulanır.
            </div>

            {activePendingEmail ? (
              <div className="info-banner">
                Yeni e-posta doğrulama bekliyor: <strong>{activePendingEmail.pendingEmail}</strong>.
                Kullanıcı onaylayana kadar aktif giriş adresi <strong>{activePendingEmail.currentEmail}</strong>{' '}
                olarak kalır.
                {activePendingEmail.sentAt
                  ? ` Son gönderim: ${formatDateTime(activePendingEmail.sentAt)}.`
                  : ''}
              </div>
            ) : null}

            <div className="form-actions">
              <button
                type="button"
                className="button ghost"
                disabled={!hasUnsavedChanges || updateMutation.isPending}
                onClick={() =>
                  setEditForm({
                    fullName: selectedUser.fullName ?? '',
                    email: selectedUser.email,
                    phone: selectedUser.phone ?? '',
                  })
                }
              >
                Geri al
              </button>
              <button
                type="submit"
                className="button primary"
                disabled={!hasUnsavedChanges || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Kaydediliyor…' : 'Bilgileri kaydet'}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
}
